import {
  ArrowUpDown,
  Clock,
  Link as LinkIcon,
  Loader2,
  MoreHorizontal,
  Power,
  Rocket,
} from 'lucide-react';

import type { ScheduledRestartInfo, TunnelInfo } from '@/types/service/clientEnvironment';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { ColumnDef } from '@tanstack/react-table';

export type ClientEnvironmentRow = {
  client: string;
  clientDescription: string;
  environment: string;
  environmentType: string;
  status: 'Online' | 'Offline' | 'In Progress' | null;
  lastDeployTime: string | null;
  config: Record<string, any> | null;
  autoShutdownEnabled?: boolean;
  isLoadingShutdown?: boolean;
  isRefreshingStatus?: boolean;
  activeTunnels: TunnelInfo[];
  scheduledRestarts: ScheduledRestartInfo[];
};

export const createColumns = (
  onActionClick: (
    action: string,
    client: string,
    environment: string,
    config?: Record<string, any> | null,
    tunnel?: TunnelInfo,
    restartRequest?: ScheduledRestartInfo,
  ) => void,
  onShutdownToggle?: (client: string, environment: string, enabled: boolean) => void,
): ColumnDef<ClientEnvironmentRow>[] => [
  {
    accessorKey: 'client',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className='justify-start px-0'
        >
          Client
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      return <div className='font-medium'>{row.getValue('client')}</div>;
    },
    enableSorting: true,
    enableGlobalFilter: true,
  },
  {
    accessorKey: 'clientDescription',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className='justify-start px-0'
        >
          Description
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    enableSorting: true,
    enableGlobalFilter: true,
  },
  {
    accessorKey: 'environment',
    header: ({ column }) => {
      return (
        <Button
          variant='ghost'
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className='justify-start px-0'
        >
          Environment
          <ArrowUpDown className='ml-2 h-4 w-4' />
        </Button>
      );
    },
    cell: ({ row }) => {
      const environment = row.getValue('environment') as string;
      const environmentType = row.original.environmentType;

      // Badge styling based on environment type
      const getBadgeStyles = () => {
        switch (environmentType) {
          case 'production':
            return 'bg-red-600 text-white border-red-700';
          case 'non-production':
            return 'bg-blue-600 text-white border-blue-700';
          case 'internal':
            return 'bg-orange-600 text-white border-orange-700';
          default:
            return '';
        }
      };

      // Get tooltip content based on environment type
      const getTooltipContent = () => {
        const categoryLabels: Record<string, string> = {
          production: 'Production Environment',
          'non-production': 'Non-Production Environment',
          internal: 'Internal Environment',
        };
        return (
          <p className='text-xs'>{categoryLabels[environmentType] || 'Unknown Environment'}</p>
        );
      };

      return (
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            <div className='inline-flex'>
              <Badge variant='outline' className={getBadgeStyles()}>
                {environment}
              </Badge>
            </div>
          </TooltipTrigger>
          <TooltipContent>{getTooltipContent()}</TooltipContent>
        </Tooltip>
      );
    },
    enableSorting: true,
    enableGlobalFilter: true,
  },
  {
    accessorKey: 'status',
    header: ({ column }) => {
      return (
        <div className='flex items-center justify-center'>
          <Button
            variant='ghost'
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Status
            <ArrowUpDown className='ml-2 h-4 w-4' />
          </Button>
        </div>
      );
    },
    cell: ({ row }) => {
      const status = row.original.status;
      const lastDeployTime = row.original.lastDeployTime;
      const client = row.original.client;
      const environment = row.original.environment;
      const config = row.original.config;
      const activeTunnels = row.original.activeTunnels || [];
      const hasTunnels = activeTunnels.length > 0;
      const isRefreshing = row.original.isRefreshingStatus;

      // Status color mapping
      const getStatusIndicator = () => {
        // Show spinner if refreshing
        if (isRefreshing) {
          return <Loader2 className='h-4 w-4 animate-spin text-blue-500' />;
        }

        switch (status) {
          case 'Online':
            return <div className='h-4 w-4 rounded-full bg-green-500' />;
          case 'Offline':
            return <div className='h-4 w-4 rounded-full bg-gray-400' />;
          case 'In Progress':
            return <div className='h-4 w-4 rounded-full bg-yellow-500 animate-pulse' />;
          default:
            return <div className='h-4 w-4 rounded-full bg-gray-300' />;
        }
      };

      // Format last deploy time
      const formatDeployTime = () => {
        if (!lastDeployTime) return 'No deployment data';

        const date = new Date(lastDeployTime);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 60) {
          return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
        } else if (diffHours < 24) {
          return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
        } else if (diffDays < 7) {
          return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        } else {
          return date.toLocaleString();
        }
      };

      const handleTunnelClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (hasTunnels && activeTunnels[0]) {
          await onActionClick('Close Remote Tunnel', client, environment, null, activeTunnels[0]);
        } else {
          await onActionClick('Create Remote Tunnel', client, environment);
        }
      };

      const handleStatusClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!isRefreshing) {
          await onActionClick('Refresh Deploy Status', client, environment);
        }
      };

      const getStatusTooltipContent = () => {
        if (isRefreshing) {
          return <p className='text-xs'>Refreshing deployment status...</p>;
        }
        return (
          <div className='space-y-1'>
            <p className='font-semibold'>{status || 'Unknown'}</p>
            <p className='text-xs text-muted-foreground'>Last Deploy: {formatDeployTime()}</p>
            <p className='text-xs text-muted-foreground mt-2'>Click to refresh status</p>
          </div>
        );
      };

      const getTunnelTooltipContent = () => {
        if (hasTunnels && activeTunnels[0]) {
          const tunnel = activeTunnels[0];
          return (
            <div className='space-y-1'>
              <p className='text-xs font-semibold text-green-500'>Active Remote Debug Tunnel</p>
              <p className='text-xs'>
                <span className='font-semibold'>Host:</span> {tunnel.debug_host}
              </p>
              <p className='text-xs'>
                <span className='font-semibold'>Port:</span> {tunnel.debug_port}
              </p>
              <p className='text-xs'>
                <span className='font-semibold'>User:</span> {tunnel.user_email}
              </p>
              <p className='text-xs text-muted-foreground'>
                Opened: {new Date(tunnel.created_at).toLocaleString()}
              </p>
              <p className='text-xs text-muted-foreground mt-2'>Click to close tunnel</p>
            </div>
          );
        }
        return <p className='text-xs'>Click to create remote debug tunnel</p>;
      };

      const isSupportEnv = environment.startsWith('support');
      const autoShutdownEnabled = row.original.autoShutdownEnabled;
      const isLoadingShutdown = row.original.isLoadingShutdown;
      const showAutoShutdown = isSupportEnv && typeof autoShutdownEnabled === 'boolean';
      const scheduledRestarts = row.original.scheduledRestarts || [];
      const hasScheduledRestart = scheduledRestarts.length > 0;
      const scheduledRestart = hasScheduledRestart ? scheduledRestarts[0] : null;

      const handleRestartClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (hasScheduledRestart && scheduledRestart) {
          await onActionClick(
            'Cancel Restart',
            client,
            environment,
            null,
            undefined,
            scheduledRestart,
          );
        } else {
          await onActionClick('Schedule Restart', client, environment);
        }
      };

      const handleDeploymentClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        await onActionClick('Start Deployment', client, environment, config);
      };

      const getRestartTooltipContent = () => {
        if (hasScheduledRestart && scheduledRestart) {
          return (
            <div className='space-y-1'>
              <p className='text-xs font-semibold text-orange-500'>Restart Scheduled</p>
              <p className='text-xs'>
                <span className='font-semibold'>Time:</span>{' '}
                {new Date(scheduledRestart.restart_timestamp).toLocaleString()}
              </p>
              <p className='text-xs'>
                <span className='font-semibold'>Initiated by:</span> {scheduledRestart.user_email}
              </p>
              <p className='text-xs text-muted-foreground mt-2'>Click to cancel</p>
            </div>
          );
        }
        return <p className='text-xs'>Click to schedule a restart</p>;
      };

      return (
        <div className='flex items-center justify-center gap-3'>
          {/* Deployment Icon */}
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <div
                className='flex items-center justify-center cursor-pointer'
                onClick={handleDeploymentClick}
              >
                <Rocket className='h-5 w-5 text-gray-400 hover:text-blue-500 transition-colors' />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className='text-xs'>Start Deployment</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <div
                className={`flex items-center justify-center ${isRefreshing ? 'cursor-wait' : 'cursor-pointer'}`}
                onClick={handleStatusClick}
              >
                {getStatusIndicator()}
              </div>
            </TooltipTrigger>
            <TooltipContent>{getStatusTooltipContent()}</TooltipContent>
          </Tooltip>

          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <div
                className='flex items-center justify-center cursor-pointer'
                onClick={handleTunnelClick}
              >
                <LinkIcon
                  className={`h-5 w-5 ${hasTunnels ? 'text-green-500' : 'text-gray-400'}`}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>{getTunnelTooltipContent()}</TooltipContent>
          </Tooltip>

          {/* Scheduled Restart Icon */}
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <div
                className='flex items-center justify-center cursor-pointer'
                onClick={handleRestartClick}
              >
                <Clock
                  className={`h-5 w-5 ${hasScheduledRestart ? 'text-orange-500' : 'text-gray-400'}`}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>{getRestartTooltipContent()}</TooltipContent>
          </Tooltip>

          {/* Always render a placeholder to maintain consistent width */}
          <div className='w-5'>
            {showAutoShutdown && (
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <div
                    className={`flex items-center justify-center ${isLoadingShutdown ? 'cursor-wait' : 'cursor-pointer'}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!isLoadingShutdown && onShutdownToggle) {
                        await onShutdownToggle(client, environment, !autoShutdownEnabled);
                      }
                    }}
                  >
                    {isLoadingShutdown ? (
                      <Loader2 className='h-5 w-5 animate-spin text-blue-500' />
                    ) : (
                      <Power
                        className={`h-5 w-5 ${autoShutdownEnabled ? 'text-green-500' : 'text-gray-400'}`}
                      />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {isLoadingShutdown ? (
                    <p className='text-xs'>Toggling auto-shutdown...</p>
                  ) : (
                    <div className='space-y-1'>
                      <p className='text-xs font-semibold'>
                        Auto-Shutdown: {autoShutdownEnabled ? 'Enabled' : 'Disabled'}
                      </p>
                      <p className='text-xs text-muted-foreground'>Click to toggle</p>
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      );
    },
    enableSorting: true,
    enableGlobalFilter: false,
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      const client = row.original.client;
      const environment = row.original.environment;
      const config = row.original.config;

      // Determine which actions are available
      const isDevClient = client === 'dev';

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='h-8 w-8 p-0'>
              <span className='sr-only'>Open menu</span>
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={() => onActionClick('Deploy Configuration', client, environment, config)}
            >
              Deploy Configuration
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onActionClick('Update Bitwarden', client, environment)}
            >
              Update Credentials
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onActionClick('Database Backup', client, environment)}>
              Initiate Database Backup
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onActionClick('Run ETL', client, environment)}>
              Run ETL
            </DropdownMenuItem>

            {isDevClient && (
              <DropdownMenuItem
                onClick={() => onActionClick('Create Dev User', client, environment)}
              >
                Create Dev User
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
